import React, { useMemo, useState, useEffect } from 'react';
import { doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { ALL_POKEMON } from '../data/teams';
import ABILITIES, { ALL_ABILITIES } from '../data/abilities';
import { ALL_ITEMS, getBaseStats } from '../data/baseStats';
import { ALL_MOVES } from '../data/selectionAdvisor';
import { getEffectiveStats } from '../data/damageCalc';
import { getSpriteUrl } from '../data/sprites';
import { useIsAdmin } from '../auth/useAdmin';
import { getAllOverrides, getOverride, useOverridesVersion } from '../data/pokemonOverrides';
import './AdminPokemonDefaults.css';

const NATURES = [
  'Adamant', 'Bold', 'Brave', 'Calm', 'Careful', 'Gentle', 'Hasty', 'Impish',
  'Jolly', 'Lax', 'Lonely', 'Mild', 'Modest', 'Naive', 'Naughty', 'Quiet',
  'Rash', 'Relaxed', 'Sassy', 'Timid', 'Hardy', 'Docile', 'Serious', 'Bashful', 'Quirky',
];

const NATURE_EFFECTS = {
  Adamant: '+Atk / -SpA', Bold: '+Def / -Atk', Brave: '+Atk / -Spe', Calm: '+SpD / -Atk',
  Careful: '+SpD / -SpA', Gentle: '+SpD / -Def', Hasty: '+Spe / -Def', Impish: '+Def / -SpA',
  Jolly: '+Spe / -SpA', Lax: '+Def / -SpD', Lonely: '+Atk / -Def', Mild: '+SpA / -Def',
  Modest: '+SpA / -Atk', Naive: '+Spe / -SpD', Naughty: '+Atk / -SpD', Quiet: '+SpA / -Spe',
  Rash: '+SpA / -SpD', Relaxed: '+Def / -Spe', Sassy: '+SpD / -Spe', Timid: '+Spe / -Atk',
  Hardy: 'Neutral', Docile: 'Neutral', Serious: 'Neutral', Bashful: 'Neutral', Quirky: 'Neutral',
};

const STAT_KEYS = ['hp', 'atk', 'def', 'spa', 'spd', 'spe'];
const STAT_LABELS = { hp: 'HP', atk: 'Atk', def: 'Def', spa: 'SpA', spd: 'SpD', spe: 'Spe' };
const STAT_COLORS = { hp: '#ef4444', atk: '#f59e0b', def: '#f97316', spa: '#3b82f6', spd: '#22c55e', spe: '#ec4899' };
const MAX_TOTAL = 66;
const MAX_PER_STAT = 32;

const EMPTY_SP = { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 };

function sumSP(sp) {
  return STAT_KEYS.reduce((s, k) => s + (sp?.[k] || 0), 0);
}

function AdminPokemonDefaults() {
  // Subscribe to override updates so the page re-renders on Firestore changes.
  useOverridesVersion();

  const isAdmin = useIsAdmin();
  const [format, setFormat] = useState('singles');
  const [search, setSearch] = useState('');
  const [onlyOverridden, setOnlyOverridden] = useState(false);
  const [expandedName, setExpandedName] = useState(null);

  const overrides = getAllOverrides(format);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = ALL_POKEMON.map((p) => p.name);
    if (q) list = list.filter((n) => n.toLowerCase().includes(q));
    if (onlyOverridden) list = list.filter((n) => !!overrides[n]);
    list.sort((a, b) => a.localeCompare(b));
    return list;
  }, [search, onlyOverridden, overrides]);

  if (!isAdmin) {
    return (
      <div className="apd-gate">
        <h2>Not authorized</h2>
        <p>This page is restricted to the admin account.</p>
      </div>
    );
  }

  const overriddenCount = Object.keys(overrides).length;

  return (
    <div className="apd">
      <header className="apd-head">
        <div>
          <h2 className="apd-title">Pokémon defaults</h2>
          <p className="apd-sub">
            Override the baked-in competitive defaults used by Battle Advisor and the team builder.
            Changes take effect immediately for all users.
          </p>
        </div>
      </header>

      <div className="apd-toolbar">
        <div className="apd-format">
          <button
            className={`apd-fmt-btn${format === 'singles' ? ' active' : ''}`}
            onClick={() => { setFormat('singles'); setExpandedName(null); }}
          >
            Singles
          </button>
          <button
            className={`apd-fmt-btn${format === 'doubles' ? ' active' : ''}`}
            onClick={() => { setFormat('doubles'); setExpandedName(null); }}
          >
            Doubles
          </button>
          <span className="apd-fmt-count">
            {overriddenCount} override{overriddenCount === 1 ? '' : 's'} in {format}
          </span>
        </div>

        <div className="apd-filters">
          <input
            type="search"
            className="apd-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${ALL_POKEMON.length} Pokémon…`}
          />
          <label className="apd-only-override">
            <input
              type="checkbox"
              checked={onlyOverridden}
              onChange={(e) => setOnlyOverridden(e.target.checked)}
            />
            <span>Only overridden</span>
          </label>
        </div>
      </div>

      <div className="apd-list">
        {filtered.length === 0 && (
          <div className="apd-empty">No Pokémon match the current filter.</div>
        )}
        {filtered.map((name) => (
          <PokemonRow
            key={`${format}:${name}`}
            name={name}
            format={format}
            isOpen={expandedName === name}
            onToggle={() => setExpandedName(expandedName === name ? null : name)}
          />
        ))}
      </div>
    </div>
  );
}

function PokemonRow({ name, format, isOpen, onToggle }) {
  const override = getOverride(name, format);
  const hasOverride = !!override;

  return (
    <article className={`apd-row${isOpen ? ' open' : ''}${hasOverride ? ' overridden' : ''}`}>
      <button className="apd-row-head" onClick={onToggle}>
        <img
          className="apd-row-sprite"
          src={getSpriteUrl(name)}
          alt=""
          onError={(e) => { e.target.style.visibility = 'hidden'; }}
        />
        <span className="apd-row-name">{name}</span>
        <span className={`apd-row-badge${hasOverride ? ' active' : ''}`}>
          {hasOverride ? 'Overridden' : 'Built-in'}
        </span>
        {hasOverride && override.ability && (
          <span className="apd-row-preview">
            {override.ability}{override.nature ? ` · ${override.nature}` : ''}
          </span>
        )}
        <span className="apd-row-caret" aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
      </button>

      {isOpen && <PokemonEditor name={name} format={format} override={override} />}
    </article>
  );
}

function PokemonEditor({ name, format, override }) {
  const base = getBaseStats(name);
  const legalAbilities = ABILITIES?.[name] || null;

  // Initialize local draft from the saved override (or blanks).
  const [draft, setDraft] = useState(() => normalizeDraft(override));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // If the underlying override changes externally (e.g. another device), resync.
  useEffect(() => {
    setDraft(normalizeDraft(override));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [override?.updatedAt]);

  const dirty = useMemo(() => !draftEquals(draft, override), [draft, override]);

  const setField = (field, value) => setDraft((d) => ({ ...d, [field]: value }));
  const setMove = (idx, value) => setDraft((d) => {
    const moves = [...(d.moves || ['', '', '', ''])];
    moves[idx] = value;
    return { ...d, moves };
  });
  const setSP = (stat, rawVal) => setDraft((d) => {
    const num = Math.max(0, Math.min(MAX_PER_STAT, parseInt(rawVal, 10) || 0));
    const current = d.statPoints?.[stat] || 0;
    const otherSum = sumSP(d.statPoints) - current;
    const allowed = Math.min(num, MAX_TOTAL - otherSum);
    return { ...d, statPoints: { ...(d.statPoints || EMPTY_SP), [stat]: Math.max(0, allowed) } };
  });

  const applyPreset = (sp) => setDraft((d) => ({ ...d, statPoints: { ...sp } }));

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const payload = buildPayload(draft);
      await setDoc(doc(db, `pokemon_defaults_${format}`, name), {
        ...payload,
        updatedAt: serverTimestamp(),
      });
      setLastSavedAt(Date.now());
    } catch (e) {
      console.error('Save override failed:', e);
      setSaveError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => { setDraft(normalizeDraft(override)); };

  const handleDelete = async () => {
    if (!override) return;
    if (!window.confirm(`Remove the ${format} override for ${name}? It will fall back to the built-in default.`)) return;
    setSaving(true);
    setSaveError('');
    try {
      await deleteDoc(doc(db, `pokemon_defaults_${format}`, name));
    } catch (e) {
      console.error('Delete override failed:', e);
      setSaveError(e.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const total = sumSP(draft.statPoints);
  const remaining = MAX_TOTAL - total;

  // Live Lv50 calculated stats for the current draft (matches what players see in-game).
  const calcStats = useMemo(() => {
    if (!base) return null;
    return getEffectiveStats({
      baseStats: base,
      nature: draft.nature || 'Hardy',
      statPoints: draft.statPoints || EMPTY_SP,
    });
  }, [base, draft.nature, draft.statPoints]);

  const presets = [
    { label: 'Atk/Spe', sp: { hp: 1, atk: 32, def: 0, spa: 0, spd: 0, spe: 32 } },
    { label: 'SpA/Spe', sp: { hp: 1, atk: 0, def: 0, spa: 32, spd: 0, spe: 32 } },
    { label: 'Phys Tank', sp: { hp: 32, atk: 1, def: 32, spa: 0, spd: 0, spe: 0 } },
    { label: 'Spec Tank', sp: { hp: 32, atk: 0, def: 0, spa: 1, spd: 32, spe: 0 } },
  ];

  return (
    <div className="apd-editor">
      <div className="apd-edit-grid">
        <label className="apd-field">
          <span className="apd-field-label">Ability</span>
          <input
            list={`apd-abilities-${name}`}
            className="apd-input"
            value={draft.ability || ''}
            onChange={(e) => setField('ability', e.target.value)}
            placeholder="(leave empty to use built-in)"
          />
          <datalist id={`apd-abilities-${name}`}>
            {(legalAbilities || ALL_ABILITIES).map((a) => <option key={a} value={a} />)}
          </datalist>
        </label>

        <label className="apd-field">
          <span className="apd-field-label">Item</span>
          <input
            list={`apd-items-${name}`}
            className="apd-input"
            value={draft.item || ''}
            onChange={(e) => setField('item', e.target.value)}
            placeholder="(leave empty to use built-in)"
          />
          <datalist id={`apd-items-${name}`}>
            {ALL_ITEMS.map((it) => <option key={it} value={it} />)}
          </datalist>
        </label>

        <label className="apd-field">
          <span className="apd-field-label">Nature</span>
          <select
            className="apd-input"
            value={draft.nature || ''}
            onChange={(e) => setField('nature', e.target.value)}
          >
            <option value="">(leave empty to use built-in)</option>
            {NATURES.map((n) => (
              <option key={n} value={n}>
                {n}{NATURE_EFFECTS[n] !== 'Neutral' ? ` — ${NATURE_EFFECTS[n]}` : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="apd-moves">
        <div className="apd-field-label">Moves</div>
        <div className="apd-moves-grid">
          {[0, 1, 2, 3].map((i) => (
            <div className="apd-move-slot" key={i}>
              <span className="apd-move-num">{i + 1}</span>
              <input
                list={`apd-moves-${name}`}
                className="apd-input"
                value={draft.moves?.[i] || ''}
                onChange={(e) => setMove(i, e.target.value)}
                placeholder="(empty)"
              />
            </div>
          ))}
          <datalist id={`apd-moves-${name}`}>
            {ALL_MOVES.map((m) => <option key={m} value={m} />)}
          </datalist>
        </div>
      </div>

      <div className="apd-sp">
        <div className="apd-sp-head">
          <span className="apd-field-label">Stat points</span>
          <span className={`apd-sp-total${remaining === 0 ? ' full' : ''}`}>
            {total}/{MAX_TOTAL}
          </span>
          <span className="apd-sp-hint" title="Live values at Lv 50 with 31 IVs + the nature you picked above. These match what players see in-game.">
            Lv 50 · 31 IVs · {draft.nature || 'neutral'} nature
          </span>
          <div className="apd-sp-presets">
            {presets.map((p) => (
              <button key={p.label} type="button" className="apd-sp-preset" onClick={() => applyPreset(p.sp)}>
                {p.label}
              </button>
            ))}
            <button type="button" className="apd-sp-preset" onClick={() => applyPreset(EMPTY_SP)}>
              Clear
            </button>
          </div>
        </div>
        <div className="apd-sp-sliders">
          {STAT_KEYS.map((s) => {
            const val = draft.statPoints?.[s] || 0;
            const lv50 = calcStats?.[s] ?? null;
            return (
              <div key={s} className="apd-sp-row">
                <span className="apd-sp-label">{STAT_LABELS[s]}</span>
                <span className="apd-sp-lv50" title="Final stat at level 50 with 31 IVs + current nature">
                  {lv50 ?? '—'}
                </span>
                <input
                  type="range"
                  min="0"
                  max={MAX_PER_STAT}
                  value={val}
                  onChange={(e) => setSP(s, e.target.value)}
                  style={{ '--fill': `${(val / MAX_PER_STAT) * 100}%`, '--color': STAT_COLORS[s] }}
                  className="apd-sp-slider"
                />
                <span className="apd-sp-val">{val}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="apd-actions">
        {saveError && <span className="apd-save-err">{saveError}</span>}
        {!saveError && lastSavedAt && !dirty && !saving && (
          <span className="apd-save-ok">Saved</span>
        )}
        <button
          type="button"
          className="apd-btn ghost"
          onClick={handleReset}
          disabled={!dirty || saving}
        >
          Reset
        </button>
        {override && (
          <button
            type="button"
            className="apd-btn danger-ghost"
            onClick={handleDelete}
            disabled={saving}
          >
            Delete override
          </button>
        )}
        <button
          type="button"
          className="apd-btn primary"
          onClick={handleSave}
          disabled={!dirty || saving}
        >
          {saving ? 'Saving…' : override ? 'Save changes' : 'Save override'}
        </button>
      </div>
    </div>
  );
}

function normalizeDraft(override) {
  return {
    ability: override?.ability || '',
    item: override?.item || '',
    nature: override?.nature || '',
    moves: Array.isArray(override?.moves) ? [
      override.moves[0] || '', override.moves[1] || '',
      override.moves[2] || '', override.moves[3] || '',
    ] : ['', '', '', ''],
    statPoints: { ...EMPTY_SP, ...(override?.statPoints || {}) },
  };
}

function draftEquals(a, b) {
  const clean = (d) => ({
    ability: d?.ability || '',
    item: d?.item || '',
    nature: d?.nature || '',
    moves: Array.isArray(d?.moves) ? d.moves.map((m) => m || '').slice(0, 4) : ['', '', '', ''],
    sp: STAT_KEYS.reduce((o, k) => { o[k] = d?.statPoints?.[k] || 0; return o; }, {}),
  });
  const A = clean(a);
  const B = clean(b);
  if (A.ability !== B.ability || A.item !== B.item || A.nature !== B.nature) return false;
  for (let i = 0; i < 4; i++) if ((A.moves[i] || '') !== (B.moves[i] || '')) return false;
  for (const k of STAT_KEYS) if (A.sp[k] !== B.sp[k]) return false;
  return true;
}

function buildPayload(draft) {
  const out = {};
  if (draft.ability?.trim()) out.ability = draft.ability.trim();
  if (draft.item?.trim()) out.item = draft.item.trim();
  if (draft.nature?.trim()) out.nature = draft.nature.trim();
  const moves = (draft.moves || []).map((m) => (m || '').trim()).filter(Boolean);
  if (moves.length) out.moves = moves.slice(0, 4);
  if (draft.statPoints && sumSP(draft.statPoints) > 0) {
    out.statPoints = STAT_KEYS.reduce((o, k) => {
      o[k] = draft.statPoints[k] || 0;
      return o;
    }, {});
  }
  return out;
}

export default AdminPokemonDefaults;
